// src/pages/Admins.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut, updatePassword, EmailAuthProvider, reauthenticateWithCredential, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, addDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../components/firebase-config.js';
import { FaUserMd, FaUserInjured, FaSignOutAlt, FaEdit, FaEye, FaEyeSlash, FaCog, FaHome, FaShieldAlt, FaUserCog, FaChartBar, FaBell, FaCalendarAlt, FaList, FaMoneyBillWave, FaArrowUp, FaArrowDown, FaSearch, FaTimes } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart } from 'recharts';
import { Modal, Button, Form, Nav, Tab } from 'react-bootstrap';
import "./Admins.css";

// Composant réutilisable pour la barre de recherche
const SearchBar = ({ placeholder, value, onChange, onClear }) => {
  return (
    <div className="ms-search-box mb-3">
      <input
        type="text"
        className="ms-search-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button className="ms-search-clear" onClick={onClear}>
          <FaTimes />
        </button>
      )}
      <span className="ms-search-icon">
        <FaSearch />
      </span>
    </div>
  );
};

// Fonctions utilitaires pour les dates
const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajuster si dimanche
  return new Date(d.setDate(diff));
};

const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  return d;
};

const startOfYear = (date) => {
  const d = new Date(date);
  d.setMonth(0, 1);
  return d;
};

const endOfMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1, 0);
  return d;
};

const subMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const format = (date, formatStr) => {
  // Implémentation simplifiée de format
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  if (formatStr === 'yyyy-MM-dd') {
    return `${year}-${month}-${day}`;
  } else if (formatStr === 'MMM yyyy') {
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${monthNames[d.getMonth()]} ${year}`;
  }
  
  return `${day}/${month}/${year}`;
};

const Admins = () => {
  const [loading, setLoading] = useState(true);
  const [structure, setStructure] = useState(null);
  const [stats, setStats] = useState({
    patients: 0,
    medecins: 0,
    rendezvous: 0
  });
  const [admin, setAdmin] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const navigate = useNavigate();

  // État pour la gestion du menu mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  
  // États pour la fonctionnalité de sélection de médecin
  const [selectedMedecin, setSelectedMedecin] = useState(null);
  const [medecinRendezvous, setMedecinRendezvous] = useState([]);
  const [showMedecinDetailsModal, setShowMedecinDetailsModal] = useState(false);
  const [medecins, setMedecins] = useState([]);
  
  // États pour la fonctionnalité de tarification
  const [showTarifModal, setShowTarifModal] = useState(false);
  const [tarifFormData, setTarifFormData] = useState({
    consultation: '',
    suiviRegulier: '',
    acteChirurgical: '',
    examensSpeciaux: ''
  });
  
  // États pour le suivi financier
  const [financialStats, setFinancialStats] = useState({
    semaine: { rdvCount: 0, revenus: 0 },
    mois: { rdvCount: 0, revenus: 0 },
    annee: { rdvCount: 0, revenus: 0 }
  });
  
  const [evolutionData, setEvolutionData] = useState({
    labels: [],
    revenus: [],
    rdvCount: []
  });
  
  // État pour stocker l'historique des tarifs
  const [tarifsHistorique, setTarifsHistorique] = useState({});

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    site: '',
    telephone: '',
    responsable: '',
    specialites: [],
    assurances: [],
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    adminNom: '',
    adminTelephone: '',
    adminCurrentPassword: '',
    adminNewPassword: '',
    adminConfirmPassword: '',
    adminEmailNotifications: false,
    adminNewPatientAlert: false,
    adminLoginAlert: false
  });

  // Ajoutez ces données de démonstration dans le composant Admins (juste après les déclarations de state)
  const [patientData, setPatientData] = useState([
    { name: 'Jan', count: 12 },
    { name: 'Fév', count: 19 },
    { name: 'Mar', count: 25 },
    { name: 'Avr', count: 34 },
    { name: 'Mai', count: 40 },
    { name: 'Juin', count: 45 },
    { name: 'Juil', count: stats.patients }
  ]);

  const [medecinData, setMedecinData] = useState([
    { name: 'Ophtalmologie', count: 4 },
    { name: 'Rétinologie', count: 3 },
    { name: 'Cornée', count: 5 },
    { name: 'Glaucome', count: 2 },
    { name: 'Pédiatrie', count: 3 }
  ]);

  // Ajout des données de rendez-vous
  const [rendezVousData, setRendezVousData] = useState([
    { name: 'Jan', total: 45, confirmes: 38 },
    { name: 'Fév', total: 52, confirmes: 43 },
    { name: 'Mar', total: 61, confirmes: 55 },
    { name: 'Avr', total: 58, confirmes: 49 },
    { name: 'Mai', total: 65, confirmes: 52 },
    { name: 'Juin', total: 74, confirmes: 67 },
    { name: 'Juil', total: 0, confirmes: 0 }
  ]);

  // États pour les barres de recherche
  const [searchInfo, setSearchInfo] = useState('');
  const [searchMedecins, setSearchMedecins] = useState('');
  const [searchStats, setSearchStats] = useState('');
  const [searchSecurity, setSearchSecurity] = useState('');
  const [searchAdmin, setSearchAdmin] = useState('');
  const [searchMedecinDetails, setSearchMedecinDetails] = useState('');
  const [searchRendezVous, setSearchRendezVous] = useState('');
  
  // Fonctions pour filtrer les éléments en fonction de la recherche
  const filteredMedecins = medecins.filter(medecin => {
    const searchTerm = searchMedecins.toLowerCase();
    if (!searchTerm) return true;
    
    return (
      medecin.nom?.toLowerCase().includes(searchTerm) ||
      medecin.specialite?.toLowerCase().includes(searchTerm) ||
      medecin.email?.toLowerCase().includes(searchTerm)
    );
  });

  // Fonction pour récupérer l'historique des tarifs d'un médecin
  const fetchTarifsHistorique = async (medecinId) => {
    try {
      const historiqueRef = doc(db, 'tarifsHistorique', medecinId);
      const historiqueDoc = await getDoc(historiqueRef);
      
      if (historiqueDoc.exists()) {
        return historiqueDoc.data();
      } else {
        return { medecinId, historique: [] };
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique des tarifs:", error);
      return { medecinId, historique: [] };
    }
  };
  
  // Fonction pour obtenir le tarif applicable à une date donnée
  const getTarifApplicable = (medecinId, dateRdv, typePrestation) => {
    // Récupérer l'historique des tarifs du médecin
    const historique = tarifsHistorique[medecinId]?.historique || [];
    
    // Si pas d'historique, utiliser le tarif actuel du médecin
    if (historique.length === 0) {
      const medecin = medecins.find(m => m.id === medecinId);
      return medecin?.tarifs?.[typePrestation] || 0;
    }
    
    // Trouver le tarif applicable à la date du rendez-vous
    let tarifApplicable = 0;
    const dateRdvObj = new Date(dateRdv);
    
    // Trier l'historique par date (du plus ancien au plus récent)
    const historiqueTriee = [...historique].sort((a, b) => 
      new Date(a.dateDebut) - new Date(b.dateDebut)
    );
    
    // Trouver le dernier tarif applicable avant la date du rendez-vous
    for (let i = historiqueTriee.length - 1; i >= 0; i--) {
      const dateDebutTarif = new Date(historiqueTriee[i].dateDebut);
      if (dateDebutTarif <= dateRdvObj) {
        tarifApplicable = historiqueTriee[i].tarifs[typePrestation] || 0;
        break;
      }
    }
    
    // Si aucun tarif n'a été trouvé (cas où le rendez-vous est plus ancien que tous les tarifs)
    // utiliser le plus ancien tarif disponible
    if (tarifApplicable === 0 && historiqueTriee.length > 0) {
      tarifApplicable = historiqueTriee[0].tarifs[typePrestation] || 0;
    }
    
    return tarifApplicable;
  };

  // Fonction pour calculer les statistiques financières pour un médecin
  const calculerStatsFinancieres = (rdvs, dateDebut, dateFin, medecinId) => {
    const rdvsPeriode = rdvs.filter(rdv => {
      const dateRdv = rdv.date instanceof Date ? rdv.date : new Date(rdv.date);
      return dateRdv >= dateDebut && dateRdv <= dateFin;
    });
    
    let totalRevenus = 0;
    
    rdvsPeriode.forEach(rdv => {
      // Ne comptabiliser que les rendez-vous non annulés
      if (rdv.statut !== 'annulé') {
        // Utiliser le tarif applicable à la date du rendez-vous
        const typePrestation = rdv.typePrestation || 'consultation';
        const tarif = getTarifApplicable(medecinId, rdv.date, typePrestation);
        totalRevenus += Number(tarif) || 0;
      }
    });
    
    return {
      rdvCount: rdvsPeriode.length,
      revenus: totalRevenus
    };
  };
  
  // Fonction pour récupérer les données d'évolution financière sur 12 mois
  const fetchEvolutionFinanciere = async (medecinId) => {
    try {
      // Récupérer les 12 derniers mois
      const months = [];
      const revenus = [];
      const rdvCount = [];
      
      const now = new Date();
      const rdvs = medecinRendezvous;
      
      // Récupérer l'historique des tarifs si pas déjà fait
      if (!tarifsHistorique[medecinId]) {
        const historique = await fetchTarifsHistorique(medecinId);
        setTarifsHistorique(prev => ({
          ...prev,
          [medecinId]: historique
        }));
      }
      
      for (let i = 11; i >= 0; i--) {
        const monthDate = subMonths(now, i);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        
        // Calculer les statistiques pour ce mois
        const stats = calculerStatsFinancieres(rdvs, monthStart, monthEnd, medecinId);
        
        months.push(format(monthDate, 'MMM yyyy'));
        revenus.push(stats.revenus);
        rdvCount.push(stats.rdvCount);
      }
      
      setEvolutionData({
        labels: months,
        revenus,
        rdvCount
      });
    } catch (error) {
      console.error("Erreur lors de la récupération des données d'évolution financière:", error);
    }
  };

  // Fonction pour récupérer les rendez-vous d'un médecin
  const fetchMedecinRendezvous = async (medecinId) => {
    try {
      setLoading(true);
      const rdvQuery = query(
        collection(db, 'rendezvous'), 
        where("medecinId", "==", medecinId)
      );
      const rdvSnapshot = await getDocs(rdvQuery);
      const rdvData = [];
      
      rdvSnapshot.forEach((doc) => {
        rdvData.push({ id: doc.id, ...doc.data() });
      });
      
      setMedecinRendezvous(rdvData);
      
      // Récupérer l'historique des tarifs
      const historique = await fetchTarifsHistorique(medecinId);
      setTarifsHistorique(prev => ({
        ...prev,
        [medecinId]: historique
      }));
      
      // Calculer les statistiques financières
      await calculerFinancialStats(rdvData, medecinId);
      
      // Récupérer les données d'évolution
      await fetchEvolutionFinanciere(medecinId);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous du médecin:", error);
      toast.error("Erreur lors de la récupération des rendez-vous");
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour calculer les statistiques financières
  const calculerFinancialStats = async (rdvs, medecinId) => {
    const now = new Date();
    const debutSemaine = startOfWeek(now);
    const debutMois = startOfMonth(now);
    const debutAnnee = startOfYear(now);
    
    const statsSemaine = calculerStatsFinancieres(rdvs, debutSemaine, now, medecinId);
    const statsMois = calculerStatsFinancieres(rdvs, debutMois, now, medecinId);
    const statsAnnee = calculerStatsFinancieres(rdvs, debutAnnee, now, medecinId);
    
    setFinancialStats({
      semaine: statsSemaine,
      mois: statsMois,
      annee: statsAnnee
    });
  };

  // Fonction pour ouvrir le modal de détails du médecin
  const handleShowMedecinDetails = async (medecin) => {
    setSelectedMedecin(medecin);
    await fetchMedecinRendezvous(medecin.id);
    setShowMedecinDetailsModal(true);
    
    // Initialiser les tarifs si disponibles
    if (medecin.tarifs) {
      setTarifFormData({
        consultation: medecin.tarifs.consultation || '',
        suiviRegulier: medecin.tarifs.suiviRegulier || '',
        acteChirurgical: medecin.tarifs.acteChirurgical || '',
        examensSpeciaux: medecin.tarifs.examensSpeciaux || ''
      });
    } else {
      setTarifFormData({
        consultation: '',
        suiviRegulier: '',
        acteChirurgical: '',
        examensSpeciaux: ''
      });
    }
  };
  
  // Fonction pour ouvrir le modal de tarification
  const handleShowTarifModal = (medecin) => {
    setSelectedMedecin(medecin);
    
    // Initialiser les tarifs si disponibles
    if (medecin.tarifs) {
      setTarifFormData({
        consultation: medecin.tarifs.consultation || '',
        suiviRegulier: medecin.tarifs.suiviRegulier || '',
        acteChirurgical: medecin.tarifs.acteChirurgical || '',
        examensSpeciaux: medecin.tarifs.examensSpeciaux || ''
      });
    } else {
      setTarifFormData({
        consultation: '',
        suiviRegulier: '',
        acteChirurgical: '',
        examensSpeciaux: ''
      });
    }
    
    setShowTarifModal(true);
  };
  
  // Fonction pour gérer les changements dans le formulaire de tarifs
  const handleTarifChange = (e) => {
    const { name, value } = e.target;
    setTarifFormData({
      ...tarifFormData,
      [name]: value
    });
  };
  
  // Fonction pour enregistrer les tarifs avec historique
  const handleSaveTarifs = async () => {
    try {
      setLoading(true);
      
      if (!selectedMedecin || !selectedMedecin.id) {
        toast.error("Aucun médecin sélectionné");
        return;
      }
      
      const medecinId = selectedMedecin.id;
      const nouveauxTarifs = {
        consultation: tarifFormData.consultation,
        suiviRegulier: tarifFormData.suiviRegulier,
        acteChirurgical: tarifFormData.acteChirurgical,
        examensSpeciaux: tarifFormData.examensSpeciaux
      };
      
      // 1. Mettre à jour les tarifs actuels du médecin
      await updateDoc(doc(db, 'medecins', medecinId), {
        tarifs: {
          ...nouveauxTarifs,
          updatedAt: new Date()
        }
      });
      
      // 2. Mettre à jour ou créer l'historique des tarifs
      const now = new Date();
      const dateFormatee = format(now, 'yyyy-MM-dd');
      
      const historiqueRef = doc(db, 'tarifsHistorique', medecinId);
      const historiqueDoc = await getDoc(historiqueRef);
      
      if (historiqueDoc.exists()) {
        // Ajouter le nouveau tarif à l'historique existant
        const historique = historiqueDoc.data().historique || [];
        historique.push({
          dateDebut: dateFormatee,
          tarifs: nouveauxTarifs,
          modifiePar: admin?.id || 'unknown',
          dateModification: new Date()
        });
        
        await updateDoc(historiqueRef, { historique });
      } else {
        // Créer un nouvel historique avec l'ID du médecin
        await setDoc(historiqueRef, {
          medecinId,
          historique: [{
            dateDebut: dateFormatee,
            tarifs: nouveauxTarifs,
            modifiePar: admin?.id || 'unknown',
            dateModification: new Date()
          }]
        });
      }
      
      // 3. Mettre à jour l'état local de l'historique des tarifs
      const updatedHistorique = await fetchTarifsHistorique(medecinId);
      setTarifsHistorique(prev => ({
        ...prev,
        [medecinId]: updatedHistorique
      }));
      
      // 4. Mettre à jour les données locales des médecins
      const updatedMedecins = medecins.map(med => {
        if (med.id === medecinId) {
          return {
            ...med,
            tarifs: {
              ...nouveauxTarifs,
              updatedAt: new Date()
            }
          };
        }
        return med;
      });
      
      setMedecins(updatedMedecins);
      setSelectedMedecin({
        ...selectedMedecin,
        tarifs: {
          ...nouveauxTarifs,
          updatedAt: new Date()
        }
      });
      
      // 5. Recalculer les statistiques financières si nécessaire
      if (medecinRendezvous.length > 0) {
        await calculerFinancialStats(medecinRendezvous, medecinId);
        await fetchEvolutionFinanciere(medecinId);
      }
      
      toast.success("Tarifs mis à jour avec succès");
      setShowTarifModal(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des tarifs:", error);
      toast.error("Erreur lors de la mise à jour des tarifs");
    } finally {
      setLoading(false);
    }
  };

  // Effet pour vérifier l'authentification et charger les données
  useEffect(() => {
    const fetchData = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user) {
          toast.error("Vous devez être connecté pour accéder à cette page");
          navigate('/');
          return;
        }

        // Vérifier si l'utilisateur est un admin
        const adminDoc = await getDoc(doc(db, 'admins', user.uid));
        if (!adminDoc.exists()) {
          toast.error("Vous n'avez pas les droits d'administrateur");
          signOut(auth);
          navigate('/');
          return;
        }

        const adminData = adminDoc.data();
        setAdmin(adminData);

        // Récupérer les informations de la structure
        const structureDoc = await getDoc(doc(db, 'structures', adminData.structureId));
        if (!structureDoc.exists()) {
          toast.error("Structure non trouvée");
          return;
        }

        const structureData = structureDoc.data();
        setStructure(structureData);
        setFormData({
          nom: structureData.nom || '',
          email: structureData.email || '',
          site: structureData.site || '',
          telephone: structureData.telephone || '',
          responsable: structureData.responsable || '',
          specialites: Array.isArray(structureData.specialites) ? structureData.specialites : [],
          assurances: Array.isArray(structureData.assurances) ? structureData.assurances : [],
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          adminNom: adminData.nom || '',
          adminTelephone: adminData.telephone || '',
          adminCurrentPassword: '',
          adminNewPassword: '',
          adminConfirmPassword: '',
          adminEmailNotifications: adminData.adminEmailNotifications || false,
          adminNewPatientAlert: adminData.adminNewPatientAlert || false,
          adminLoginAlert: adminData.adminLoginAlert || false
        });

        // Récupérer les statistiques
        await fetchStats(adminData.structureId);
        
        // Récupérer la liste des médecins
        await fetchMedecins(adminData.structureId);

      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        toast.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Fonction pour récupérer les médecins
  const fetchMedecins = async (structureId) => {
    try {
      const medecinsQuery = query(collection(db, 'medecins'), where("structureId", "==", structureId));
      const medecinsSnapshot = await getDocs(medecinsQuery);
      const medecinsData = [];
      
      medecinsSnapshot.forEach((doc) => {
        medecinsData.push({ id: doc.id, ...doc.data() });
      });
      
      setMedecins(medecinsData);
    } catch (error) {
      console.error("Erreur lors de la récupération des médecins:", error);
      toast.error("Erreur lors de la récupération des médecins");
    }
  };

  // Fonction pour récupérer les statistiques
  const fetchStats = async (structureId) => {
    try {
      // Statistiques de base
      const patientsQuery = query(collection(db, 'patients'), where("structureId", "==", structureId));
      const patientsSnapshot = await getDocs(patientsQuery);
      
      const medecinsQuery = query(collection(db, 'medecins'), where("structureId", "==", structureId));
      const medecinsSnapshot = await getDocs(medecinsQuery);

      // Nouvelle requête pour les rendez-vous
      const rendezVousQuery = query(collection(db, 'rendezvous'), where("structureId", "==", structureId));
      const rendezVousSnapshot = await getDocs(rendezVousQuery);

      setStats({
        patients: patientsSnapshot.size,
        medecins: medecinsSnapshot.size,
        rendezvous: rendezVousSnapshot.size  // Ajout du nombre de rendez-vous
      });

      // Données pour les graphiques
      
      // 1. Patients par mois (croissance mensuelle)
      const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      
      // Récupérer tous les patients et grouper par date de création
      const patientsByMonth = [];
      let patientCount = 0;
      
      patientsSnapshot.forEach(doc => {
        const patientData = doc.data();
        if (patientData.createdAt) {
          const patientMonth = new Date(patientData.createdAt.toDate()).getMonth();
          // Accumuler les patients par mois pour montrer la croissance
          if (!patientsByMonth[patientMonth]) {
            patientsByMonth[patientMonth] = 0;
          }
          patientsByMonth[patientMonth]++;
        }
      });
      
      // Créer les données pour le graphique de patients
      const patientGraphData = [];
      for (let i = 0; i < 7; i++) {
        // Récupérer les 7 derniers mois
        const monthIndex = (currentMonth - 6 + i + 12) % 12;
        patientCount += patientsByMonth[monthIndex] || 0;
        patientGraphData.push({
          name: monthNames[monthIndex],
          count: patientCount
        });
      }
      
      setPatientData(patientGraphData.length > 0 ? patientGraphData : [
        { name: monthNames[currentMonth], count: patientsSnapshot.size }
      ]);
      
      // 2. Médecins par spécialité
      const specialtiesCounts = {};
      
      medecinsSnapshot.forEach(doc => {
        const medecinData = doc.data();
        if (medecinData.specialite) {
          if (!specialtiesCounts[medecinData.specialite]) {
            specialtiesCounts[medecinData.specialite] = 0;
          }
          specialtiesCounts[medecinData.specialite]++;
        }
      });
      
      const medecinGraphData = Object.keys(specialtiesCounts).map(specialty => ({
        name: specialty.length > 12 ? specialty.substring(0, 10) + '...' : specialty,
        count: specialtiesCounts[specialty]
      }));
      
      setMedecinData(medecinGraphData.length > 0 ? medecinGraphData : [
        { name: 'Médecins', count: medecinsSnapshot.size }
      ]);

      // 3. Rendez-vous par mois avec statut
      // Initialiser les compteurs pour chaque mois
      const rdvByMonth = Array(12).fill().map(() => ({ total: 0, confirmes: 0 }));
      
      // Compter les rendez-vous par mois et par statut
      rendezVousSnapshot.forEach(doc => {
        const rdvData = doc.data();
        if (rdvData.date) {
          const rdvDate = new Date(rdvData.date);
          const rdvMonth = rdvDate.getMonth();
          
          rdvByMonth[rdvMonth].total++;
          
          if (rdvData.statut === 'confirmé') {
            rdvByMonth[rdvMonth].confirmes++;
          }
        }
      });
      
      // Créer les données pour le graphique de rendez-vous (derniers 7 mois)
      const rdvGraphData = [];
      for (let i = 0; i < 7; i++) {
        const monthIndex = (currentMonth - 6 + i + 12) % 12;
        rdvGraphData.push({
          name: monthNames[monthIndex],
          total: rdvByMonth[monthIndex].total,
          confirmes: rdvByMonth[monthIndex].confirmes
        });
      }
      
      setRendezVousData(rdvGraphData.length > 0 ? rdvGraphData : [
        { name: monthNames[currentMonth], total: rendezVousSnapshot.size, confirmes: 0 }
      ]);
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      toast.error("Erreur lors de la récupération des statistiques");
    }
  };

  // 2. Ajoutez un useEffect pour mettre à jour patientData quand stats.patients change
  useEffect(() => {
    if (stats.patients > 0 && patientData.length > 0) {
      // Mettre à jour le dernier point de données pour refléter le total actuel
      const updatedPatientData = [...patientData];
      updatedPatientData[updatedPatientData.length - 1].count = stats.patients;
      setPatientData(updatedPatientData);
    }
  }, [stats.patients]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSpecialiteChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentSpecialites = Array.isArray(prev.specialites) ? prev.specialites : [];
      return {
        ...prev,
        specialites: checked 
          ? [...currentSpecialites, value]
          : currentSpecialites.filter(spec => spec !== value)
      };
    });
  };

  const handleAssuranceChange = (e) => {
    const { value, checked } = e.target;
    let newAssurances = [...formData.assurances];
    
    if (checked) {
      if (!newAssurances.includes(value)) {
        newAssurances.push(value);
      }
    } else {
      newAssurances = newAssurances.filter(assur => assur !== value);
    }
    
    setFormData({ ...formData, assurances: newAssurances });
  };

  const handleResetStructurePassword = async () => {
    try {
      setLoading(true);
      
      if (!formData.email) {
        toast.error("Email de la structure non disponible");
        return;
      }
      
      const auth = getAuth();
      await sendPasswordResetEmail(auth, formData.email);
      
      toast.success("Un email de réinitialisation de mot de passe a été envoyé à " + formData.email);
      
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email de réinitialisation:", error);
      toast.error("Erreur lors de l'envoi de l'email de réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      if (!admin?.structureId) {
        toast.error("ID de structure manquant");
        setLoading(false);
        return;
      }

      // Mise à jour des informations de la structure
      await updateDoc(doc(db, 'structures', admin.structureId), {
        nom: formData.nom,
        site: formData.site,
        telephone: formData.telephone,
        responsable: formData.responsable,
        specialites: formData.specialites,
        assurances: formData.assurances
      });

      // Si un nouveau mot de passe est défini, le mettre à jour
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }

        if (!formData.currentPassword) {
          toast.error("Veuillez entrer votre mot de passe actuel");
          setLoading(false);
          return;
        }

        const auth = getAuth();
        const user = auth.currentUser;
        
        // Réauthentifier l'utilisateur avant de changer le mot de passe
          const credential = EmailAuthProvider.credential(user.email, formData.currentPassword);
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, formData.newPassword);
          
          // Réinitialiser les champs de mot de passe
          setFormData({
            ...formData,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          toast.success("Mot de passe mis à jour avec succès");
      }

      // Rafraîchir les données
      const structureDoc = await getDoc(doc(db, 'structures', admin.structureId));
      setStructure(structureDoc.data());
      
      toast.success("Modifications enregistrées avec succès");
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des modifications:", error);
      toast.error("Erreur lors de la sauvegarde des modifications");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAdminChanges = async () => {
    try {
      setLoading(true);
      
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        toast.error("Vous devez être connecté pour effectuer cette action");
        return;
      }
      
      // Mise à jour des informations de l'administrateur
      await updateDoc(doc(db, 'admins', user.uid), {
        nom: formData.adminNom || admin?.nom,
        telephone: formData.adminTelephone || admin?.telephone,
        adminEmailNotifications: formData.adminEmailNotifications || admin?.adminEmailNotifications,
        adminNewPatientAlert: formData.adminNewPatientAlert || admin?.adminNewPatientAlert,
        adminLoginAlert: formData.adminLoginAlert || admin?.adminLoginAlert
      });
      
      // Mise à jour du mot de passe si nécessaire
      if (formData.adminNewPassword) {
        if (formData.adminNewPassword !== formData.adminConfirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        
        if (!formData.adminCurrentPassword) {
          toast.error("Veuillez entrer votre mot de passe actuel");
          setLoading(false);
          return;
        }
        
        try {
          // Réauthentifier l'utilisateur avant de changer le mot de passe
          const credential = EmailAuthProvider.credential(user.email, formData.adminCurrentPassword);
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, formData.adminNewPassword);
          
          // Réinitialiser les champs de mot de passe
          setFormData({
            ...formData,
            adminCurrentPassword: '',
            adminNewPassword: '',
            adminConfirmPassword: ''
          });
          
          toast.success("Mot de passe administrateur mis à jour avec succès");
        } catch (error) {
          console.error("Erreur lors de la mise à jour du mot de passe:", error);
          toast.error("Mot de passe actuel incorrect");
          setLoading(false);
          return;
        }
      }

      // Mettre à jour les données locales
      const adminDoc = await getDoc(doc(db, 'admins', user.uid));
      setAdmin(adminDoc.data());
      
      toast.success("Profil administrateur mis à jour avec succès");
      setEditMode(false);
    } catch (error) {
      console.error("Erreur lors de la sauvegarde des modifications:", error);
      toast.error("Erreur lors de la sauvegarde des modifications");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      toast.success("Déconnexion réussie");
      navigate('/');
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      toast.error("Erreur lors de la déconnexion");
    }
  };

  // Fonction pour formater la date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };
  
  // Fonction pour formater le prix
  const formatPrix = (prix) => {
    if (!prix) return '—';
    return `${Number(prix).toLocaleString('fr-FR')} FCFA`;
  };

  // Composant pour afficher le tableau de bord financier
  const FinancialDashboard = ({ medecinId, medecinNom }) => {
    return (
      <div className="financial-dashboard mb-4">
        <h5 className="mb-3">Suivi financier</h5>
        
        <div className="row">
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">Cette semaine</h6>
              </div>
              <div className="card-body">
                <h3 className="text-primary mb-2">{formatPrix(financialStats.semaine.revenus)}</h3>
                <div className="d-flex justify-content-between small text-muted">
                  <span>{financialStats.semaine.rdvCount} rendez-vous</span>
                  <span>{financialStats.semaine.rdvCount > 0 ? 
                    formatPrix(financialStats.semaine.revenus / financialStats.semaine.rdvCount) : '—'} / rdv</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">Ce mois</h6>
              </div>
              <div className="card-body">
                <h3 className="text-success mb-2">{formatPrix(financialStats.mois.revenus)}</h3>
                <div className="d-flex justify-content-between small text-muted">
                  <span>{financialStats.mois.rdvCount} rendez-vous</span>
                  <span>{financialStats.mois.rdvCount > 0 ? 
                    formatPrix(financialStats.mois.revenus / financialStats.mois.rdvCount) : '—'} / rdv</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-4">
            <div className="card h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">Cette année</h6>
              </div>
              <div className="card-body">
                <h3 className="text-info mb-2">{formatPrix(financialStats.annee.revenus)}</h3>
                <div className="d-flex justify-content-between small text-muted">
                  <span>{financialStats.annee.rdvCount} rendez-vous</span>
                  <span>{financialStats.annee.rdvCount > 0 ? 
                    formatPrix(financialStats.annee.revenus / financialStats.annee.rdvCount) : '—'} / rdv</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Composant pour afficher l'évolution financière
  const FinancialEvolution = () => {
    return (
      <div className="financial-evolution mb-4">
        <h5 className="mb-3">Évolution financière sur 12 mois</h5>
        
        <div className="card">
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={evolutionData.labels.map((month, index) => ({
                name: month,
                revenus: evolutionData.revenus[index],
                rdvCount: evolutionData.rdvCount[index]
              }))}>
                <XAxis dataKey="name" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip formatter={(value, name) => {
                  if (name === "revenus") return [formatPrix(value).replace(' FCFA', ''), "Revenus"];
                  return [value, "Rendez-vous"];
                }} />
                <Legend />
                <CartesianGrid strokeDasharray="3 3" />
                <Bar yAxisId="right" dataKey="rdvCount" fill="#8884d8" name="Nombre de RDV" />
                <Line yAxisId="left" type="monotone" dataKey="revenus" stroke="#ff7300" name="Revenus" />
              </ComposedChart>
            </ResponsiveContainer>
            
            <div className="row mt-3">
              <div className="col-md-4">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Total sur 12 mois:</span>
                  <span className="fw-bold">{formatPrix(evolutionData.revenus.reduce((a, b) => a + b, 0))}</span>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Moyenne mensuelle:</span>
                  <span className="fw-bold">
                    {formatPrix(evolutionData.revenus.length > 0 ? 
                      evolutionData.revenus.reduce((a, b) => a + b, 0) / evolutionData.revenus.length : 0)}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Tendance:</span>
                  <span className={`fw-bold ${
                    evolutionData.revenus.length >= 2 && evolutionData.revenus[evolutionData.revenus.length - 1] > 
                    evolutionData.revenus[evolutionData.revenus.length - 2] ? 'text-success' : 'text-danger'
                  }`}>
                    {evolutionData.revenus.length >= 2 && evolutionData.revenus[evolutionData.revenus.length - 1] > 
                      evolutionData.revenus[evolutionData.revenus.length - 2] ? (
                      <><FaArrowUp className="me-1" /> En hausse</>
                    ) : (
                      <><FaArrowDown className="me-1" /> En baisse</>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fonction pour filtrer les rendez-vous
  const filteredRendezVous = medecinRendezvous.filter(rdv => {
    if (!searchRendezVous) return true;
    const searchTerm = searchRendezVous.toLowerCase();
    
    return (
      rdv.patientNom?.toLowerCase().includes(searchTerm) ||
      rdv.statut?.toLowerCase().includes(searchTerm) ||
      (rdv.typePrestation && rdv.typePrestation.toLowerCase().includes(searchTerm)) ||
      (formatDate(rdv.date).toLowerCase().includes(searchTerm)) ||
      (rdv.heure && rdv.heure.toLowerCase().includes(searchTerm))
    );
  });

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ms-app">
      {/* Overlay pour fermer le menu mobile */}
      <div 
        className={`ms-mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} 
        onClick={() => setMobileMenuOpen(false)}
      ></div>
      
      {/* Bouton de menu mobile */}
      <button className="ms-mobile-menu-toggle d-md-none" onClick={toggleMobileMenu}>
        <div className="ms-hamburger">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </button>
      
      {/* Menu latéral pour mobile */}
      <div className={`ms-mobile-sidebar d-md-none ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="ms-mobile-sidebar-header">
          <div className="ms-logo">
            <FaUserCog size={28} />
          </div>
          <div className="ms-title">
            <h5>Cabinet Admin</h5>
            <p className="small text-muted">{structure?.nom}</p>
          </div>
          <button 
            className="ms-close-button" 
            onClick={() => setMobileMenuOpen(false)}
            style={{marginLeft: 'auto', background: 'none', border: 'none'}}
          >
            ×
          </button>
        </div>
        
        <div className="ms-mobile-sidebar-items">
          <button 
            className={`ms-mobile-sidebar-item ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => { setActiveTab('info'); setMobileMenuOpen(false); }}
          >
            <FaHome size={20} />
            <span>Accueil</span>
          </button>
          
          <button 
            className={`ms-mobile-sidebar-item ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => { setActiveTab('admin'); setMobileMenuOpen(false); }}
          >
            <FaUserCog size={20} />
            <span>Mon profil</span>
          </button>
          
          <button 
            className={`ms-mobile-sidebar-item ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => { setActiveTab('security'); setMobileMenuOpen(false); }}
          >
            <FaShieldAlt size={20} />
            <span>Sécurité</span>
          </button>
          
          <button 
            className={`ms-mobile-sidebar-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => { setActiveTab('stats'); setMobileMenuOpen(false); }}
          >
            <FaChartBar size={20} />
            <span>Statistiques</span>
          </button>
          
          <button 
            className={`ms-mobile-sidebar-item ${activeTab === 'medecins' ? 'active' : ''}`}
            onClick={() => { setActiveTab('medecins'); setMobileMenuOpen(false); }}
          >
            <FaUserMd size={20} />
            <span>Médecins</span>
          </button>
        </div>
        
        <div className="ms-mobile-sidebar-footer">
          <button 
            className="ms-mobile-sidebar-item ms-logout"
            onClick={handleLogout}
          >
            <FaSignOutAlt size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
      
      {/* Sidebar navigation - caché sur mobile */}
      <div className="ms-sidebar d-none d-md-flex">
        <div className="ms-sidebar-header">
          <div className="ms-logo">
            <FaUserCog size={28} />
          </div>
          <div className="ms-title">
            <h5>Cabinet Admin</h5>
            <p className="small text-muted">{structure?.nom}</p>
          </div>
        </div>
        
        <div className="ms-nav">
          <button 
            className={`ms-nav-item ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <FaHome size={20} />
            <span>Accueil</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            <FaUserCog size={20} />
            <span>Mon profil</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FaShieldAlt size={20} />
            <span>Sécurité</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <FaChartBar size={20} />
            <span>Statistiques</span>
          </button>
          
          <button 
            className={`ms-nav-item ${activeTab === 'medecins' ? 'active' : ''}`}
            onClick={() => setActiveTab('medecins')}
          >
            <FaUserMd size={20} />
            <span>Médecins</span>
          </button>
        </div>
        
        <div className="ms-sidebar-footer">
          <button 
            className="ms-nav-item ms-logout"
            onClick={handleLogout}
          >
            <FaSignOutAlt size={20} />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="ms-content">
        <ToastContainer 
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />

        {/* Header */}
        <div className="ms-header">
          <div className="ms-header-title">
            <h4> {admin?.nom}</h4>
            <p className="text-muted">{admin?.email}</p>
          </div>
          <div className="ms-header-actions">
            <button className="ms-action-button">
              <FaBell size={18} />
            </button>
            <div className="ms-user-avatar">
            {admin?.nom?.charAt(0)}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="ms-container">
          {/* Page d'accueil (Info) avec barre de recherche */}
          {activeTab === 'info' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="ms-section-title mb-0">Tableau de bord</h2>
                <SearchBar
                  placeholder="Rechercher..."
                  value={searchInfo}
                  onChange={setSearchInfo}
                  onClear={() => setSearchInfo('')}
                />
              </div>
              
              <div className="ms-stats-grid">
                <div className="ms-stat-card">
                  <div className="ms-stat-icon patients">
                    <FaUserInjured size={24} />
                  </div>
                  <div className="ms-stat-content">
                    <h3>{stats.patients}</h3>
                    <p>Patients</p>
                  </div>
                </div>
          
                <div className="ms-stat-card">
                  <div className="ms-stat-icon doctors">
                    <FaUserMd size={24} />
                  </div>
                  <div className="ms-stat-content">
                    <h3>{stats.medecins}</h3>
                    <p>Médecins</p>
                  </div>
                </div>
                
                <div className="ms-stat-card">
                  <div className="ms-stat-icon appointments">
                    <FaCalendarAlt size={24} />
                  </div>
                  <div className="ms-stat-content">
                    <h3>{stats.rendezvous}</h3>
                    <p>Rendez-vous</p>
                  </div>
                </div>
              </div>

              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Informations de la structure</h4>
                <button 
                    className="ms-edit-button"
                    onClick={() => {
                      if (editMode) {
                        handleSaveChanges();
                      } else {
                        setEditMode(true);
                      }
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="ms-spinner"></span>
                    ) : (
                      <FaEdit size={16} />
                    )}
                    <span>{editMode ? "Enregistrer" : "Modifier"}</span>
                  </button>
                </div>

                <div className="ms-card-body">
                  <div className="ms-form-grid">
                    <div className="ms-form-group">
                      <label htmlFor="nomStructure">Nom de la structure</label>
                      <input
                        type="text"
                        id="nomStructure"
                        name="nom"
                        className="ms-input"
                        value={formData.nom}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="emailStructure">Email</label>
                      <input
                        type="email"
                        id="emailStructure"
                        name="email"
                        className="ms-input"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={true}
                      />
                    </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="telephoneStructure">Téléphone</label>
                      <input
                        type="tel"
                        id="telephoneStructure"
                        name="telephone"
                        className="ms-input"
                        value={formData.telephone}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  
                    <div className="ms-form-group">
                      <label htmlFor="siteStructure">Site web</label>
                      <input
                        type="text"
                        id="siteStructure"
                        name="site"
                        className="ms-input"
                        value={formData.site}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="responsableStructure">Responsable</label>
                      <input
                        type="text"
                        id="responsableStructure"
                        name="responsable"
                        className="ms-input"
                        value={formData.responsable}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                  </div>

                  <div className="ms-specialties">
                    <label className="ms-label">Spécialités</label>
                        {editMode ? (
                      <div className="ms-checkbox-group">
                            {["Ophtalmologie générale", "Chirurgie réfractive", "Chirurgie de la cataracte", 
                              "Rétinologie", "Glaucome", "Ophtalmologie pédiatrique", 
                              "Neuro-ophtalmologie", "Cornée et surface oculaire", "Contactologie"].map(specialite => (
                          <label className="ms-checkbox" key={specialite}>
                                <input
                                  type="checkbox"
                                  value={specialite}
                                  checked={formData.specialites.includes(specialite)}
                                  onChange={handleSpecialiteChange}
                                />
                            <span>{specialite}</span>
                                </label>
                        ))}
                            </div>
                        ) : (
                      <div className="ms-tags">
                            {Array.isArray(formData.specialites) ? formData.specialites.map((specialite, index) => (
                          <span className="ms-tag" key={index}>{specialite}</span>
                            )) : null}
                          </div>
                        )}
                  </div>
                  
                  <div className="ms-insurances">
                    <label className="ms-label">Assurances acceptées</label>
                        {editMode ? (
                      <div className="ms-checkbox-group">
                            {['CNAMGS', 'MAAB', 'Ascoma', 'Allianz', 'CNSS', 'NSIA', 'Sanlam'].map(assurance => (
                          <label className="ms-checkbox" key={assurance}>
                                <input
                                  type="checkbox"
                                  value={assurance}
                                  checked={formData.assurances.includes(assurance)}
                                  onChange={handleAssuranceChange}
                                />
                            <span>{assurance}</span>
                                </label>
                        ))}
                            </div>
                        ) : (
                      <div className="ms-tags">
                            {formData.assurances.map((assurance, index) => (
                          <span className="ms-tag blue" key={index}>{assurance}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
            </>
          )}
          
          {/* Page Sécurité avec barre de recherche */}
          {activeTab === 'security' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="ms-section-title mb-0">Sécurité</h2>
                <SearchBar
                  placeholder="Rechercher un paramètre..."
                  value={searchSecurity}
                  onChange={setSearchSecurity}
                  onClear={() => setSearchSecurity('')}
                />
              </div>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Réinitialisation du mot de passe</h4>
                </div>
                <div className="ms-card-body">
                  <div className="ms-alert info">
                    <div className="ms-alert-icon">
                      <FaEye size={18} />
                    </div>
                    <div className="ms-alert-content">
                      Un email de réinitialisation sera envoyé à {formData.email}
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <button 
                      className="ms-button primary"
                      onClick={handleResetStructurePassword}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="ms-spinner"></span>
                      ) : (
                        <>Envoyer un lien de réinitialisation</>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Changer le mot de passe</h4>
                </div>
                <div className="ms-card-body">
                  <div className="ms-form-group">
                    <label htmlFor="currentPassword">Mot de passe actuel</label>
                    <div className="ms-password-input">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        id="currentPassword"
                        name="currentPassword"
                        className="ms-input"
                        value={formData.currentPassword}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        className="ms-password-toggle"
                        onClick={() => setPasswordVisible(!passwordVisible)}
                      >
                        {passwordVisible ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="newPassword">Nouveau mot de passe</label>
                    <div className="ms-password-input">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        id="newPassword"
                        name="newPassword"
                        className="ms-input"
                        value={formData.newPassword}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                    <div className="ms-password-input">
                      <input
                        type={passwordVisible ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        className="ms-input"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <button 
                      className="ms-button primary"
                      onClick={handleSaveChanges}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="ms-spinner"></span>
                      ) : (
                        <>Changer le mot de passe</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Page Profil Admin avec barre de recherche */}
          {activeTab === 'admin' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="ms-section-title mb-0">Profil administrateur</h2>
                <SearchBar
                  placeholder="Rechercher un paramètre..."
                  value={searchAdmin}
                  onChange={setSearchAdmin}
                  onClear={() => setSearchAdmin('')}
                />
              </div>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Informations personnelles</h4>
                </div>
                <div className="ms-card-body">
                  <div className="ms-form-grid">
                    <div className="ms-form-group">
                      <label htmlFor="adminNom">Nom</label>
                      <input
                        type="text"
                        id="adminNom"
                        name="adminNom"
                        className="ms-input"
                        value={formData.adminNom}
                        onChange={handleChange}
                      />
                    </div>
                    
                   
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="adminEmail">Email</label>
                    <input
                      type="email"
                      id="adminEmail"
                      className="ms-input"
                      value={admin?.email || ''}
                      disabled
                    />
                  </div>
                  
                  <div className="ms-form-group">
                    <label htmlFor="adminTelephone">Téléphone</label>
                    <input
                      type="tel"
                      id="adminTelephone"
                      name="adminTelephone"
                      className="ms-input"
                      value={formData.adminTelephone}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Notifications</h4>
                </div>
                <div className="ms-card-body">
                  <div className="ms-toggle-group">
                    <div className="ms-toggle-item">
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          name="adminEmailNotifications"
                          checked={formData.adminEmailNotifications}
                          onChange={(e) => setFormData({...formData, adminEmailNotifications: e.target.checked})}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                      <div className="ms-toggle-label">
                        <h5>Notifications par email</h5>
                        <p>Recevoir des notifications par email</p>
                      </div>
                    </div>
                    
                    <div className="ms-toggle-item">
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          name="adminNewPatientAlert"
                          checked={formData.adminNewPatientAlert}
                          onChange={(e) => setFormData({...formData, adminNewPatientAlert: e.target.checked})}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                      <div className="ms-toggle-label">
                        <h5>Nouveaux patients</h5>
                        <p>Être notifié lors de l'inscription d'un nouveau patient</p>
                      </div>
                    </div>
                    
                    <div className="ms-toggle-item">
                      <label className="ms-toggle">
                        <input
                          type="checkbox"
                          name="adminLoginAlert"
                          checked={formData.adminLoginAlert}
                          onChange={(e) => setFormData({...formData, adminLoginAlert: e.target.checked})}
                        />
                        <span className="ms-toggle-slider"></span>
                      </label>
                      <div className="ms-toggle-label">
                        <h5>Alertes de connexion</h5>
                        <p>Être notifié des nouvelles connexions à votre compte</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="ms-form-group">
                    <button 
                      className="ms-button primary"
                      onClick={handleSaveAdminChanges}
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="ms-spinner"></span>
                      ) : (
                        <>Enregistrer les modifications</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Page Statistiques avec barre de recherche */}
          {activeTab === 'stats' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="ms-section-title mb-0">Statistiques détaillées</h2>
                <SearchBar
                  placeholder="Rechercher des statistiques..."
                  value={searchStats}
                  onChange={setSearchStats}
                  onClear={() => setSearchStats('')}
                />
              </div>
              
              <div className="ms-stats-grid detailed">
                <div className="ms-stat-card large">
                  <div className="ms-stat-header">
                    <h4>Patients</h4>
                    <span className="ms-stat-number">{stats.patients}</span>
                  </div>
                  <div className="ms-stat-body">
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={patientData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Area type="monotone" dataKey="count" stroke="#0078d4" fill="#e8f5ff" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                
                <div className="ms-stat-card large">
                  <div className="ms-stat-header">
                    <h4>Médecins</h4>
                    <span className="ms-stat-number">{stats.medecins}</span>
                  </div>
                  <div className="ms-stat-body">
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={medecinData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="#107c41" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Nouvelle carte pour les rendez-vous */}
                <div className="ms-stat-card large">
                  <div className="ms-stat-header">
                    <h4>Rendez-vous</h4>
                    <span className="ms-stat-number">{stats.rendezvous || 0}</span>
                  </div>
                  <div className="ms-stat-body">
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={rendezVousData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="total" name="Total" stroke="#8884d8" />
                        <Line type="monotone" dataKey="confirmes" name="Confirmés" stroke="#82ca9d" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Page médecins avec barre de recherche */}
          {activeTab === 'medecins' && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="ms-section-title mb-0">Liste des médecins</h2>
                <SearchBar
                  placeholder="Rechercher un médecin..."
                  value={searchMedecins}
                  onChange={setSearchMedecins}
                  onClear={() => setSearchMedecins('')}
                />
              </div>
              
              <div className="ms-card">
                <div className="ms-card-header">
                  <h4>Médecins de la structure</h4>
                </div>
                <div className="ms-card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Spécialité</th>
                          <th>Contact</th>
                          <th>Tarifs</th>
                          <th>Rendez-vous</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMedecins.map(medecin => (
                          <tr key={medecin.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="ms-user-avatar me-2">
                                 {medecin.nom?.charAt(0)}
                                </div>
                                <div>
                                  <div className="fw-bold">Dr. {medecin.nom}</div>
                                </div>
                              </div>
                            </td>
                            <td>{medecin.specialite}</td>
                            <td>
                              <div>{medecin.telephone}</div>
                              <div className="text-muted small">{medecin.email}</div>
                            </td>
                            <td>
                              {medecin.tarifs ? (
                                <div className="d-flex flex-column">
                                  <span className="badge bg-light text-dark mb-1">
                                    Consultation: {formatPrix(medecin.tarifs.consultation)}
                                  </span>
                                  <button 
                                    className="ms-button small success"
                                    onClick={() => handleShowTarifModal(medecin)}
                                  >
                                    <FaMoneyBillWave size={14} className="me-1" />
                                    Modifier tarifs
                                  </button>
                                </div>
                              ) : (
                                <button 
                                  className="ms-button small primary"
                                  onClick={() => handleShowTarifModal(medecin)}
                                >
                                  <FaMoneyBillWave size={14} className="me-1" />
                                  Définir tarifs
                                </button>
                              )}
                            </td>
                            <td className="text-center">
                              <button 
                                className="ms-button small info"
                                onClick={() => handleShowMedecinDetails(medecin)}
                              >
                                <FaList size={14} className="me-1" />
                                Voir détails
                              </button>
                            </td>
                            <td>
                             
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {filteredMedecins.length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-muted">Aucun médecin ne correspond à votre recherche</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Modal pour les détails du médecin */}
      <Modal 
        show={showMedecinDetailsModal} 
        onHide={() => {
          setShowMedecinDetailsModal(false);
          setSearchMedecinDetails('');
          setSearchRendezVous('');
        }}
        size="lg"
        dialogClassName="modal-mobile-responsive"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMedecin && (
              <div>
                <h5 className="mb-0">Dr.  {selectedMedecin.nom}</h5>
                <p className="text-muted small mb-0">{selectedMedecin.specialite}</p>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : (
            <>
              <Tab.Container defaultActiveKey="info">
                <Nav variant="tabs" className="mb-3 flex-nowrap small-tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="info" className="px-3 py-2">Infos</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="rdv" className="px-3 py-2">Rendez-vous</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="finance" className="px-3 py-2">Finances</Nav.Link>
                  </Nav.Item>
                </Nav>
                
                <Tab.Content>
                  <Tab.Pane eventKey="info">
                    <SearchBar
                      placeholder="Rechercher dans les informations..."
                      value={searchMedecinDetails}
                      onChange={setSearchMedecinDetails}
                      onClear={() => setSearchMedecinDetails('')}
                    />
                    
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6 className="mb-2">Informations de contact</h6>
                        <p className="mb-1"><strong>Email:</strong> {selectedMedecin?.email}</p>
                        <p className="mb-3"><strong>Téléphone:</strong> {selectedMedecin?.telephone}</p>
                      </div>
                      <div className="col-md-6 mt-3 mt-md-0">
                        <h6 className="mb-2">Disponibilités</h6>
                        <div className="d-flex flex-wrap">
                          {selectedMedecin && Array.isArray(selectedMedecin.disponibilites) ? (
                            selectedMedecin.disponibilites.map((dispo, index) => (
                              <span key={index} className="badge bg-light text-dark me-1 mb-1">
                                {dispo.jour}: {dispo.heureDebut}-{dispo.heureFin}
                              </span>
                            ))
                          ) : (
                            selectedMedecin && Object.entries(selectedMedecin.disponibilites || {}).map(([jour, dispo]) => (
                              dispo.actif && (
                                <span key={jour} className="badge bg-light text-dark me-1 mb-1">
                                  {jour.charAt(0).toUpperCase() + jour.slice(1)}: {dispo.debut}-{dispo.fin}
                                </span>
                              )
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Section des tarifs */}
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">Tarifs</h6>
                        <button 
                          className="ms-button small success"
                          onClick={() => handleShowTarifModal(selectedMedecin)}
                        >
                          <FaMoneyBillWave size={14} className="me-1" />
                          {selectedMedecin?.tarifs ? "Modifier tarifs" : "Définir tarifs"}
                        </button>
                      </div>
                      
                      {selectedMedecin?.tarifs ? (
                        <div className="table-responsive">
                          <table className="table table-bordered table-sm d-none d-md-table">
                            <thead>
                              <tr>
                                <th>Type de prestation</th>
                                <th>Tarif</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Consultation</td>
                                <td>{formatPrix(selectedMedecin.tarifs.consultation)}</td>
                              </tr>
                              <tr>
                                <td>Suivi régulier</td>
                                <td>{formatPrix(selectedMedecin.tarifs.suiviRegulier)}</td>
                              </tr>
                              <tr>
                                <td>Acte chirurgical</td>
                                <td>{formatPrix(selectedMedecin.tarifs.acteChirurgical)}</td>
                              </tr>
                              <tr>
                                <td>Examens spéciaux</td>
                                <td>{formatPrix(selectedMedecin.tarifs.examensSpeciaux)}</td>
                              </tr>
                            </tbody>
                          </table>
                          
                          <p className="small text-muted">
                            Dernière mise à jour: {selectedMedecin.tarifs.updatedAt ? 
                              formatDate(selectedMedecin.tarifs.updatedAt) : 'Non spécifiée'}
                          </p>
                        </div>
                      ) : (
                        <div className="alert alert-warning">
                          Aucun tarif n'a été défini pour ce médecin.
                        </div>
                      )}
                    </div>
                    
                    {/* Historique des tarifs */}
                    {tarifsHistorique[selectedMedecin?.id] && tarifsHistorique[selectedMedecin.id].historique?.length > 1 && (
                      <div className="mb-4">
                        <h6>Historique des tarifs</h6>
                        <div className="table-responsive">
                          <table className="table table-sm table-striped">
                            <thead>
                              <tr>
                                <th>Date d'application</th>
                                <th>Consultation</th>
                                <th>Suivi régulier</th>
                                <th>Acte chirurgical</th>
                                <th>Examens spéciaux</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tarifsHistorique[selectedMedecin.id].historique.map((entry, index) => (
                                <tr key={index}>
                                  <td>{entry.dateDebut}</td>
                                  <td>{formatPrix(entry.tarifs.consultation)}</td>
                                  <td>{formatPrix(entry.tarifs.suiviRegulier)}</td>
                                  <td>{formatPrix(entry.tarifs.acteChirurgical)}</td>
                                  <td>{formatPrix(entry.tarifs.examensSpeciaux)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="small text-muted">
                          Note: Les rendez-vous sont facturés selon le tarif en vigueur à leur date de prise.
                        </p>
                      </div>
                    )}
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="rdv">
                    <SearchBar
                      placeholder="Rechercher un rendez-vous..."
                      value={searchRendezVous}
                      onChange={setSearchRendezVous}
                      onClear={() => setSearchRendezVous('')}
                    />
                    
                    <h6>Rendez-vous ({filteredRendezVous.length}/{medecinRendezvous.length})</h6>
                    {filteredRendezVous.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Heure</th>
                              <th>Patient</th>
                              <th>Type</th>
                              <th>Tarif applicable</th>
                              <th>Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRendezVous.map(rdv => (
                              <tr key={rdv.id}>
                                <td>{formatDate(rdv.date)}</td>
                                <td>{rdv.heure}</td>
                                <td>{rdv.patientNom}</td>
                                <td>{rdv.typePrestation || 'Consultation'}</td>
                                <td>
                                  {formatPrix(getTarifApplicable(
                                    selectedMedecin.id, 
                                    rdv.date, 
                                    rdv.typePrestation || 'consultation'
                                  ))}
                                </td>
                                <td>
                                  <span className={`badge ${
                                    rdv.statut === 'confirmé' ? 'bg-success' :
                                    rdv.statut === 'annulé' ? 'bg-danger' :
                                    'bg-warning'
                                  }`}>
                                    {rdv.statut}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="alert alert-info">
                        {searchRendezVous 
                          ? "Aucun rendez-vous ne correspond à votre recherche." 
                          : "Aucun rendez-vous trouvé pour ce médecin."}
                      </div>
                    )}
                    
                    {/* Statistiques du médecin */}
                    <div className="mt-4">
                      <h6>Statistiques</h6>
                      <div className="row">
                        <div className="col-md-4">
                          <div className="card text-center p-3">
                            <h3>{medecinRendezvous.length}</h3>
                            <p className="mb-0">Total des rendez-vous</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="card text-center p-3">
                            <h3>{medecinRendezvous.filter(rdv => rdv.statut === 'confirmé').length}</h3>
                            <p className="mb-0">Rendez-vous confirmés</p>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="card text-center p-3">
                            <h3>{medecinRendezvous.filter(rdv => rdv.statut === 'annulé').length}</h3>
                            <p className="mb-0">Rendez-vous annulés</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab.Pane>
                  
                  <Tab.Pane eventKey="finance">
                    <SearchBar
                      placeholder="Rechercher dans les finances..."
                      value={searchMedecinDetails}
                      onChange={setSearchMedecinDetails}
                      onClear={() => setSearchMedecinDetails('')}
                    />
                    {selectedMedecin && (
                      <>
                        <FinancialDashboard 
                          medecinId={selectedMedecin.id} 
                          medecinNom={` ${selectedMedecin.nom}`} 
                        />
                        
                        <FinancialEvolution />
                      </>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowMedecinDetailsModal(false);
            setSearchMedecinDetails('');
            setSearchRendezVous('');
          }}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Modal pour définir/modifier les tarifs */}
      <Modal 
        show={showTarifModal} 
        onHide={() => setShowTarifModal(false)}
        dialogClassName="modal-mobile-responsive"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMedecin && (
              <div>
                <h5 className="mb-0">Tarifs - Dr.  {selectedMedecin.nom}</h5>
                <p className="text-muted small mb-0">{selectedMedecin.specialite}</p>
              </div>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : (
            <Form>
              <div className="mb-3 d-none d-md-block">
                <p className="text-muted">
                  Définissez les tarifs pour les différentes prestations proposées par ce médecin.
                  Ces tarifs seront visibles par les patients lors de la prise de rendez-vous.
                </p>
                {tarifsHistorique[selectedMedecin?.id] && tarifsHistorique[selectedMedecin.id].historique?.length > 0 && (
                  <div className="alert alert-info">
                    <strong>Note:</strong> La modification des tarifs n'affectera que les nouveaux rendez-vous.
                    Les rendez-vous existants conserveront le tarif applicable à leur date de prise.
                  </div>
                )}
              </div>
              
              <div className="d-block d-md-none">
                <div className="alert alert-info py-2 small">
                  Les nouveaux tarifs s'appliqueront uniquement aux futurs rendez-vous.
                </div>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Consultation standard (FCFA)</Form.Label>
                <Form.Control
                  type="number"
                  name="consultation"
                  value={tarifFormData.consultation}
                  onChange={handleTarifChange}
                  placeholder="Ex: 25000"
                />
                <Form.Text className="text-muted small">
                  Prix d'une consultation simple
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Suivi régulier (FCFA)</Form.Label>
                <Form.Control
                  type="number"
                  name="suiviRegulier"
                  value={tarifFormData.suiviRegulier}
                  onChange={handleTarifChange}
                  placeholder="Ex: 15000"
                />
                <Form.Text className="text-muted small">
                  Prix pour un rendez-vous de suivi
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Acte chirurgical (FCFA)</Form.Label>
                <Form.Control
                  type="number"
                  name="acteChirurgical"
                  value={tarifFormData.acteChirurgical}
                  onChange={handleTarifChange}
                  placeholder="Ex: 150000"
                />
                <Form.Text className="text-muted small">
                  Prix moyen pour une intervention chirurgicale
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Examens spéciaux (FCFA)</Form.Label>
                <Form.Control
                  type="number"
                  name="examensSpeciaux"
                  value={tarifFormData.examensSpeciaux}
                  onChange={handleTarifChange}
                  placeholder="Ex: 35000"
                />
                <Form.Text className="text-muted small">
                  Prix pour les examens complémentaires
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTarifModal(false)}>
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveTarifs}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Enregistrement...
              </>
            ) : (
              "Enregistrer les tarifs"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Admins;